import {
  Classification,
  ExtractionRequest,
  FieldWriteBackRequest,
  InsightRequest,
} from 'dashboard/models/document';
import { VendorConfig } from 'dashboard/models/vendor-config';
import { KoreanCertificateType, Verification } from 'dashboard/models/verification';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { DocumentAllowedFileType } from 'lib/interfaces/StepsConfig';
import {
  SelfieCaptureFileType,
  SelfieCaptureOption,
  SelfieVideoCaptureMethod,
} from 'lib/interfaces/verificationConfig';
import { GovernmentIdFileType } from 'verify/lib/interfaces/CaptureOption';

import { InquiryTemplateVersion } from './inquiry-template-version';
import { VerificationCheckConfig } from './verification-check-config/verification-check-config';

export interface VerificationTemplateVersionSchema {
  // Only the token and status is returned for the inquiry template versions
  'inquiry-template-versions': { data: Partial<InquiryTemplateVersion>[] };
}

export enum Disclaimer {
  Default = 'default',
  None = 'none',
}

export enum Feature {
  BarcodeExtraction = 'barcode_extraction',
  BiometricsProcessing = 'biometrics_processing',
  BiometricsStorage = 'biometrics_storage',
  MrzExtraction = 'mrz_extraction',
  NfcPortraitDetection = 'nfc_portrait_extraction',
  PublicFigureDetection = 'public_figure_detection',

  Verification = 'verification',
  Extraction = 'extraction',
  Classification = 'classification',
}

const FeatureVtvTypeMap: Record<Feature, string[]> = {
  [Feature.BarcodeExtraction]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.BiometricsProcessing]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.BiometricsStorage]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.MrzExtraction]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.NfcPortraitDetection]: [ApiModel.VerificationTemplateVersionGovernmentIdNfc],
  [Feature.PublicFigureDetection]: [
    ApiModel.VerificationTemplateVersionGovernmentId,
    ApiModel.VerificationTemplateVersionSelfie,
  ],

  [Feature.Verification]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.Extraction]: [ApiModel.VerificationTemplateVersionGovernmentId],
  [Feature.Classification]: [ApiModel.VerificationTemplateVersionGovernmentId],
};

export function getFeaturesForVtv(
  vtvType:
    | ApiModel.VerificationTemplateVersionGovernmentId
    | ApiModel.VerificationTemplateVersionSelfie
    | ApiModel.VerificationTemplateVersionGovernmentIdNfc,
): Feature[] {
  return (Object.keys(FeatureVtvTypeMap) as Feature[]).filter((feature) =>
    FeatureVtvTypeMap[feature].includes(vtvType),
  );
}

interface FeatureProperties {
  description: string;
  configurable: boolean;
}

export const FeaturePropertiesMap: Record<Feature, FeatureProperties> = {
  [Feature.BarcodeExtraction]: {
    description: 'Scan barcodes in submitted images',
    configurable: true,
  },
  [Feature.BiometricsProcessing]: {
    description: 'Use biometrics present in submitted images for face comparisons',
    configurable: true,
  },
  [Feature.BiometricsStorage]: {
    description: 'Store biometrics for 1-to-N face comparisons',
    configurable: true,
  },
  [Feature.MrzExtraction]: {
    description: 'Parse MRZs present in submitted images',
    configurable: true,
  },
  [Feature.NfcPortraitDetection]: {
    description: 'Store the selfie image present in scanned NFC chips',
    configurable: false,
  },
  [Feature.PublicFigureDetection]: {
    description: 'Compare faces in submitted images against known public figures',
    configurable: false,
  },
  [Feature.Verification]: {
    description: 'Run verification checks',
    configurable: false,
  },
  [Feature.Extraction]: {
    description: 'Extract text-based information from the provided image',
    configurable: false,
  },
  [Feature.Classification]: {
    description: 'Classify the provided image',
    configurable: false,
  },
};

export type DisabledFeatureConfig = {
  // Null are only valid initial values.
  'country-code': CountryCode | 'default' | null;
  subdivision: string | 'default' | null;
  'id-classes': GovernmentIdClass[] | 'default' | null;
  'max-age-to-disable'?: number;
  'disabled-features': Feature[];
};

export enum GovernmentIdFeature {
  Autoclassification = 'autoclassification',
  JpExtractChangeOfAddress = 'jp_extract_change_of_address',
  UsEnhancedAddressExtraction = 'us_enhanced_address_extraction',
  EuVehicleClassExtraction = 'eu_vehicle_class_extraction',
}

export enum GovernmentIdCaptureFileType {
  Image = 'image',
  Video = 'video',
}

export enum GovernmentIdVideoCaptureMethod {
  Stream = 'stream',
  Upload = 'upload',
}

export enum GovernmentIdScannerFeature {
  FallbackVendors = 'fallback_vendors',
  TransformBack = 'transform_back',
  TransformSharp = 'transform_sharp',
  LayoutWhenBarcode = 'layout_when_barcode',
  UsAddressNormalize = 'us_address_normalize',
  LayoutV2 = 'layout_v2',
}

export type GovernmentIdEnabledIdClassByCountries = Record<
  CountryCodeLower,
  Array<GovernmentIdEnabledIdClassByCountryEntry>
>;

export enum RequiredDevice {
  None = 'none',
  Mobile = 'mobile',
}

export enum GovernmentIdSide {
  Front = 'front',
  Back = 'back',
  BarcodePdf417 = 'barcode_pdf417',
  FrontOrBack = 'front_or_back',
  PassportSignature = 'passport_signature',
}

export interface GovernmentIdEnabledIdClassByCountryEntry {
  class: GovernmentIdClass;
  'requires-device': RequiredDevice;
  'requires-sides': GovernmentIdSide[];
  'accepted-classes': GovernmentIdClass[];
}

export enum InputField {
  Name = 'name',
  Birthdate = 'birthdate',
  Address = 'address',
  IdentificationNumberLast4 = 'identification_number_last_4',
  IdentificationNumberFull9 = 'identification_number_full_9',
  PhoneNumber = 'phone_number',
  EmailAddress = 'email_address',
}

export interface AcceptedMatchResults {
  name?: MatchResult;
  'name-first'?: MatchResult;
  'name-last'?: MatchResult;
  birthdate?: MatchResult;
  address?: MatchResult;
  'social-security-number'?: MatchResult;
  'phone-number'?: MatchResult;
  'email-address'?: MatchResult;
}

export enum MatchResult {
  Full = 'full',
  Partial = 'partial',
  None = 'none',
  Missing = 'missing',
}

export enum DocumentAcceptedDocumentTypes {
  Check = 'check',
  Invoice = 'invoice',
  Paystub = 'paystub',
  TaxDocument = 'tax_document',
  SocialSecurityCardUnrestricted = 'social_security_card_unrestricted',
  SocialSecurityCardRestricted = 'social_security_card_restricted',
}

export enum GovernmentIdAttribute {
  NameFirst = 'name_first',
  NameLast = 'name_last',
  Birthdate = 'birthdate',
  IdentificationNumber = 'identification_number',
  AddressStreet1 = 'address_street_1',
  AddressStreet2 = 'address_street_2',
  AddressCity = 'address_city',
  AddressSubdivision = 'address_subdivision',
  AddressPostalCode = 'address_postal_code',
}

export enum PhoneNumberInfoMatch {
  None = 'none',
  Name = 'name',
  NameAndBirthdate = 'name_and_birthdate',
  NameAndSsn = 'name_and_ssn',
}

export enum PhoneNumberSignal {
  Telesign = 'telesign',
}

export enum SelfieMatch {
  ProfileAndCenter = 'profile_and_center',
  OnlyCenter = 'only_center',
  ConfigurablePoses = 'configurable_poses',
}

export enum IdCompareOption {
  FirstPassed = 'first_passed',
  LastPassed = 'last_passed',
  Last = 'last',
}

export enum DatabaseAddressAutocompleteMethod {
  None = 'none',
  GoogleMaps = 'google_maps',
}

// NOTE: this is only used in certain configs like GovernmentIdEnabledIdClassByCountries
// due to key inflection.
export type CountryCodeLower = Lowercase<CountryCode>;

/**
 * See https://www.notion.so/withpersona/Persona-Restricted-Country-Policy-b93c8496311f40dd917f566c325957c4.
 */
export const RESTRICTED_COUNTRIES: CountryCodeLower[] = [
  'cd', // Democratic Republic of Congo
  'cn', // China, Peoples Republic of
  'cu', // Cuba
  'ir', // Iran (Islamic Republic of)
  'kp', // Korea (Democratic People's Republic of)
  'ru', // Russian Federation
  'sy', // Syrian Arab Republic
];

// VerificationTemplateVersion Configs
export interface VerificationTemplateVersionConfigAamva {
  'verification-checks': VerificationCheckConfig[];
}
export interface VerificationTemplateVersionConfigBusinessWebsite {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigCertificateKorea {
  'accepted-certificates': KoreanCertificateType[];
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabase {
  'accepted-match-results': AcceptedMatchResults;
  'address-autocomplete-method': DatabaseAddressAutocompleteMethod;
  'input-fields': InputField[];
  'verification-checks': VerificationCheckConfig[];
  'vendor-configurations': {};
}

export interface VerificationTemplateVersionConfigDatabaseAadhaar {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseBusiness {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseBusinessFootprint {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseEcbsv {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabasePhoneCarrier {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseSerpro {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseTin {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDatabaseVatNumber {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDigitalIdFinnishTrustNetwork {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDigitalIdOneId {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDigitalIdSerpro {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDigitalIdSingpass {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDigitalIdUkSharecode {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigDocument {
  'enabled-file-types': DocumentAllowedFileType[];
  classifications: Classification[];
  extractions: ExtractionRequest[];
  insights: InsightRequest[];
  'insight-limit': number;
  'verification-checks': VerificationCheckConfig[];
  'document-kind'?: string;
  'field-schemas'?: FieldSchema[];
  'field-write-backs'?: FieldWriteBackRequest[];
}

export interface VerificationTemplateVersionConfigEmailAddress {
  'verification-checks': VerificationCheckConfig[];
  'num-attempts': number;
}

export interface VerificationTemplateVersionConfigGovernmentId {
  'accepted-match-results': AcceptedMatchResults;
  'enabled-id-classes-by-country': GovernmentIdEnabledIdClassByCountries;
  'enabled-skip-unknown': boolean;
  'external-disabled-feature-configs': DisabledFeatureConfig[];
  'scanner-skip-features': GovernmentIdScannerFeature[];
  'verification-checks': VerificationCheckConfig[];
  disclaimer: Disclaimer;
  'device-handoff-options': DeviceHandoffOption[];
  'enabled-capture-file-types': GovernmentIdCaptureFileType[];
  'enabled-file-types': GovernmentIdFileType[];
  'enabled-features': GovernmentIdFeature[];
  'video-capture-methods': GovernmentIdVideoCaptureMethod[];
}

export interface VerificationTemplateVersionConfigGovernmentIdNfc {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigMobileDriversLicense {
  'verification-checks': VerificationCheckConfig[];
}

export interface VerificationTemplateVersionConfigPhoneNumber {
  'enabled-signals': PhoneNumberSignal[];
  'require-info-match': PhoneNumberInfoMatch;
  'verification-checks': VerificationCheckConfig[];
  'num-attempts': number;
}

export interface VerificationTemplateVersionConfigSelfie {
  'enabled-capture-options-desktop': SelfieCaptureOption[];
  'enabled-capture-options-mobile': SelfieCaptureOption[];
  'enabled-capture-file-types': SelfieCaptureFileType[];
  'external-disabled-feature-configs': DisabledFeatureConfig[];
  'require-selfie-match': SelfieMatch;
  'verification-checks': VerificationCheckConfig[];
  'require-strict-selfie-capture': boolean;
  'video-capture-methods': SelfieVideoCaptureMethod[];
}

export const VTMPLV_TEMPLATE_TYPE_MAP: Record<
  VerificationTemplateVersion['type'],
  VerificationTemplate['type']
> = Object.fromEntries(
  Object.keys(ApiModel)
    .filter((key) => key.startsWith('VerificationTemplateVersion'))
    .map((key) => [
      ApiModel[key as keyof typeof ApiModel] as VerificationTemplateVersion['type'],
      ApiModel[
        key.replace('VerificationTemplateVersion', 'VerificationTemplate') as keyof typeof ApiModel
      ] as VerificationTemplate['type'],
    ]),
) as Record<VerificationTemplateVersion['type'], VerificationTemplate['type']>;

export const VTMPLV_VERIFICATION_TYPE_MAP: Record<
  VerificationTemplateVersion['type'],
  Verification['type']
> = Object.fromEntries(
  Object.keys(ApiModel)
    .filter((key) => key.startsWith('VerificationTemplateVersion'))
    .map((key) => [
      ApiModel[key as keyof typeof ApiModel] as VerificationTemplateVersion['type'],
      ApiModel[
        key.replace('VerificationTemplateVersion', 'Verification') as keyof typeof ApiModel
      ] as Verification['type'],
    ]),
) as Record<VerificationTemplateVersion['type'], Verification['type']>;

export type VerificationTemplateVersionConfig =
  | VerificationTemplateVersionConfigAamva
  | VerificationTemplateVersionConfigCertificateKorea
  | VerificationTemplateVersionConfigDatabase
  | VerificationTemplateVersionConfigDatabaseEcbsv
  | VerificationTemplateVersionConfigDatabasePhoneCarrier
  | VerificationTemplateVersionConfigDocument
  | VerificationTemplateVersionConfigEmailAddress
  | VerificationTemplateVersionConfigGovernmentId
  | VerificationTemplateVersionConfigGovernmentIdNfc
  | VerificationTemplateVersionConfigMobileDriversLicense
  | VerificationTemplateVersionConfigPhoneNumber
  | VerificationTemplateVersionConfigSelfie;

export enum VerificationTemplateVersionStatus {
  Draft = 'draft',
  Published = 'published',
}

export interface VerificationTemplateVersionAttributes {
  name?: string;
  'template-name'?: string;
  status: VerificationTemplateVersionStatus;
  'verification-checks': VerificationCheckConfig[];
  'created-at': string;
  'updated-at': string;
  'archived-at'?: string;
}

export interface VerificationTemplateVersionAamvaAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigAamva {}

export interface VerificationTemplateVersionBusinessWebsiteAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigBusinessWebsite {}

export interface VerificationTemplateVersionCertificateKoreaAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigCertificateKorea {}

export interface VerificationTemplateVersionDatabaseAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabase {
  'vendor-configurations': Record<string, VendorConfig[]>;
}

export interface VerificationTemplateVersionDatabaseAadhaarAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseAadhaar {}

export interface VerificationTemplateVersionDatabaseStandardAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabase {}

export interface VerificationTemplateVersionDatabaseBusinessAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseBusiness {}

export interface VerificationTemplateVersionDatabaseBusinessFootprintAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseBusinessFootprint {}

export interface VerificationTemplateVersionDatabaseBusinessFootprintAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseBusinessFootprint {}

export interface VerificationTemplateVersionDatabaseEcbsvAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseEcbsv {}

export interface VerificationTemplateVersionDatabasePhoneCarrierAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabasePhoneCarrier {}

export interface VerificationTemplateVersionDatabaseSerproAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseSerpro {}

export interface VerificationTemplateVersionDatabaseTinAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseTin {}

export interface VerificationTemplateVersionDocumentAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDocument {}

export interface VerificationTemplateVersionEmailAddressAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigEmailAddress {}

export interface VerificationTemplateVersionGovernmentIdAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigGovernmentId {}

export interface VerificationTemplateVersionGovernmentIdNfcAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigGovernmentIdNfc {}

export interface VerificationTemplateVersionMobileDriversLicenseAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigMobileDriversLicense {}

export interface VerificationTemplateVersionPhoneNumberAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigPhoneNumber {}

export interface VerificationTemplateVersionSelfieAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigSelfie {}

export interface VerificationTemplateVersionDatabaseVatNumberAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDatabaseVatNumber {}

export interface VerificationTemplateVersionDigitalIdFinnishTrustNetworkAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDigitalIdFinnishTrustNetwork {}

export interface VerificationTemplateVersionDigitalIdOneIdAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDigitalIdOneId {}

export interface VerificationTemplateVersionDigitalIdSerproAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDigitalIdSerpro {}

export interface VerificationTemplateVersionDigitalIdSingpassAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDigitalIdSingpass {}

export interface VerificationTemplateVersionDigitalIdUkSharecodeAttributes
  extends VerificationTemplateVersionAttributes,
    VerificationTemplateVersionConfigDigitalIdUkSharecode {}

export interface VerificationTemplateVersionRelationships {
  'verification-template': ApiRelationship<ApiModel.VerificationTemplate>;
}

export type VerificationTemplateVersionAamva = ApiResource<
  VerificationTemplateVersionAamvaAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionAamva
>;
export type VerificationTemplateVersionBusinessWebsite = ApiResource<
  VerificationTemplateVersionBusinessWebsiteAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionBusinessWebsite
>;

export type VerificationTemplateVersionCertificateKorea = ApiResource<
  VerificationTemplateVersionCertificateKoreaAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionCertificateKorea
>;
export type VerificationTemplateVersionDatabase = ApiResource<
  VerificationTemplateVersionDatabaseAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabase
>;
export type VerificationTemplateVersionDatabaseAadhaar = ApiResource<
  VerificationTemplateVersionDatabaseAadhaarAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseAadhaar
>;

export type VerificationTemplateVersionDatabaseStandard = ApiResource<
  VerificationTemplateVersionDatabaseStandardAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseStandard
>;

export type VerificationTemplateVersionDatabaseBusiness = ApiResource<
  VerificationTemplateVersionDatabaseBusinessAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseBusiness
>;
export type VerificationTemplateVersionDatabaseBusinessFootprint = ApiResource<
  VerificationTemplateVersionDatabaseBusinessFootprintAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint
>;
export type VerificationTemplateVersionDatabaseEcbsv = ApiResource<
  VerificationTemplateVersionDatabaseEcbsvAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseEcbsv
>;
export type VerificationTemplateVersionDatabasePhoneCarrier = ApiResource<
  VerificationTemplateVersionDatabasePhoneCarrierAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabasePhoneCarrier
>;
export type VerificationTemplateVersionDatabaseSerpro = ApiResource<
  VerificationTemplateVersionDatabaseSerproAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseSerpro
>;
export type VerificationTemplateVersionDatabaseTin = ApiResource<
  VerificationTemplateVersionDatabaseTinAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseTin
>;
export type VerificationTemplateVersionDatabaseVatNumber = ApiResource<
  VerificationTemplateVersionDatabaseVatNumberAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDatabaseVatNumber
>;
export type VerificationTemplateVersionDigitalIdFinnishTrustNetwork = ApiResource<
  VerificationTemplateVersionDigitalIdFinnishTrustNetworkAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDigitalIdFinnishTrustNetwork
>;
export type VerificationTemplateVersionDigitalIdOneId = ApiResource<
  VerificationTemplateVersionDigitalIdOneIdAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDigitalIdOneId
>;
export type VerificationTemplateVersionDigitalIdSerpro = ApiResource<
  VerificationTemplateVersionDigitalIdSerproAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDigitalIdSerpro
>;
export type VerificationTemplateVersionDigitalIdSingpass = ApiResource<
  VerificationTemplateVersionDigitalIdSingpassAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDigitalIdSingpass
>;
export type VerificationTemplateVersionDigitalIdUkSharecode = ApiResource<
  VerificationTemplateVersionDigitalIdUkSharecodeAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDigitalIdUkSharecode
>;
export type VerificationTemplateVersionDocument = ApiResource<
  VerificationTemplateVersionDocumentAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionDocument
>;
export type VerificationTemplateVersionEmailAddress = ApiResource<
  VerificationTemplateVersionEmailAddressAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionEmailAddress
>;
export type VerificationTemplateVersionGovernmentId = ApiResource<
  VerificationTemplateVersionGovernmentIdAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionGovernmentId
>;
export type VerificationTemplateVersionGovernmentIdNfc = ApiResource<
  VerificationTemplateVersionGovernmentIdNfcAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionGovernmentIdNfc
>;
export type VerificationTemplateVersionMobileDriversLicense = ApiResource<
  VerificationTemplateVersionMobileDriversLicenseAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionMobileDriversLicense
>;
export type VerificationTemplateVersionPhoneNumber = ApiResource<
  VerificationTemplateVersionPhoneNumberAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionPhoneNumber
>;
export type VerificationTemplateVersionSelfie = ApiResource<
  VerificationTemplateVersionSelfieAttributes,
  VerificationTemplateVersionRelationships,
  ApiModel.VerificationTemplateVersionSelfie
>;
export type VerificationTemplateVersion =
  | VerificationTemplateVersionAamva
  | VerificationTemplateVersionBusinessWebsite
  | VerificationTemplateVersionCertificateKorea
  | VerificationTemplateVersionDatabase
  | VerificationTemplateVersionDatabaseAadhaar
  | VerificationTemplateVersionDatabaseStandard
  | VerificationTemplateVersionDatabaseBusiness
  | VerificationTemplateVersionDatabaseBusinessFootprint
  | VerificationTemplateVersionDatabaseEcbsv
  | VerificationTemplateVersionDatabasePhoneCarrier
  | VerificationTemplateVersionDatabaseSerpro
  | VerificationTemplateVersionDatabaseTin
  | VerificationTemplateVersionDatabaseVatNumber
  | VerificationTemplateVersionDigitalIdFinnishTrustNetwork
  | VerificationTemplateVersionDigitalIdOneId
  | VerificationTemplateVersionDigitalIdSerpro
  | VerificationTemplateVersionDigitalIdSingpass
  | VerificationTemplateVersionDigitalIdUkSharecode
  | VerificationTemplateVersionDocument
  | VerificationTemplateVersionEmailAddress
  | VerificationTemplateVersionGovernmentId
  | VerificationTemplateVersionGovernmentIdNfc
  | VerificationTemplateVersionMobileDriversLicense
  | VerificationTemplateVersionPhoneNumber
  | VerificationTemplateVersionSelfie;
